import React from "react";
import Seo from "../components/Seo";

const PrivacyPolicy = () => {
  return (
    <main className="page legal-page">
      <Seo
        title="Privacy Policy"
        description="Learn how OctoGames collects, stores, and processes user data."
        path="/legal/privacy"
        keywords="octogames privacy policy, data policy, personal data"
      />
      <div className="container legal-container">
        <article className="legal-card">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-intro">
            Last updated: February 14, 2026
          </p>
          <p className="legal-copy">
            This Privacy Policy explains how OctoGames ("Website") collects, uses, stores, and
            protects personal data. By using the Website, you agree to this Privacy Policy.
          </p>

          <h2 className="legal-section-title">1. Who We Are</h2>
          <p className="legal-copy">
            OctoGames is operated by a private individual located in Ukraine ("Operator", "we",
            "us", or "our"). The Website is available to users worldwide.
          </p>

          <h2 className="legal-section-title">2. Data We Collect</h2>
          <p className="legal-copy">
            We collect only data needed to operate and improve the Website. Depending on how you
            use the service, this may include:
          </p>
          <ul className="legal-list">
            <li>Account data (for example, email address and authentication identifiers).</li>
            <li>Profile data (for example, avatar choice and account preferences).</li>
            <li>Usage data (for example, favorites, game sessions, and feature interactions).</li>
            <li>
              Technical data (for example, IP address, browser/device type, OS, and diagnostics).
            </li>
            <li>
              Support communications (for example, email messages sent to support@octogames.io).
            </li>
          </ul>

          <h2 className="legal-section-title">3. How We Collect Data</h2>
          <p className="legal-copy">
            We collect data directly from you (such as when you create an account or contact
            support), automatically through your use of the Website, and from integrated
            third-party services.
          </p>

          <h2 className="legal-section-title">4. How We Use Data</h2>
          <p className="legal-copy">
            We use personal data to:
          </p>
          <ul className="legal-list">
            <li>Provide core Website features and maintain your account.</li>
            <li>Display games, favorites, and personalized functionality.</li>
            <li>Improve reliability, performance, and security.</li>
            <li>Respond to support requests and legal notices.</li>
            <li>Prevent abuse, fraud, and unauthorized activity.</li>
            <li>Comply with legal obligations.</li>
          </ul>

          <h2 className="legal-section-title">5. Cookies and Similar Technologies</h2>
          <p className="legal-copy">
            We and our service providers may use cookies, local storage, and similar technologies
            to keep sessions active, remember settings, and measure performance. You can control
            cookies through browser settings, but disabling them may affect Website functionality.
          </p>

          <h2 className="legal-section-title">6. Third-Party Services</h2>
          <p className="legal-copy">
            The Website uses third-party services to operate features and deliver embedded content.
            These may include:
          </p>
          <ul className="legal-list">
            <li>Authentication and cloud infrastructure providers.</li>
            <li>Game content providers, including OctoGames.io partners.</li>
            <li>Analytics, advertising, and security providers.</li>
          </ul>
          <p className="legal-copy">
            Third-party providers may process data under their own privacy policies and terms.
            We do not control third-party policies.
          </p>

          <h2 className="legal-section-title">7. International Data Processing</h2>
          <p className="legal-copy">
            Because the Website is available globally and uses international service providers,
            your data may be processed in countries outside your country of residence, including
            countries with different data protection standards.
          </p>

          <h2 className="legal-section-title">8. Data Retention</h2>
          <p className="legal-copy">
            We keep personal data only for as long as necessary for the purposes described in this
            Privacy Policy, including account management, security, legal compliance, and dispute
            resolution.
          </p>

          <h2 className="legal-section-title">9. Data Sharing</h2>
          <p className="legal-copy">
            We do not sell personal data. We may share data only when needed to operate the
            Website, comply with law, enforce our Terms, protect rights/safety, or as part of a
            lawful business/technical transfer.
          </p>

          <h2 className="legal-section-title">10. Security</h2>
          <p className="legal-copy">
            We implement reasonable technical and organizational measures to protect data against
            unauthorized access, loss, misuse, or alteration. However, no system is completely
            secure, and absolute security cannot be guaranteed.
          </p>

          <h2 className="legal-section-title">11. Children&apos;s Privacy</h2>
          <p className="legal-copy">
            The Website is not intentionally directed to children where parental consent is required
            by applicable law. If you believe a child has provided personal data without proper
            consent, contact us and we will review and take appropriate action.
          </p>

          <h2 className="legal-section-title">12. Your Rights</h2>
          <p className="legal-copy">
            Depending on your jurisdiction, you may have rights to access, correct, delete, or
            restrict processing of your personal data, and to object to certain processing.
            You may also have the right to withdraw consent where processing relies on consent.
          </p>
          <p className="legal-copy">
            To make a request, contact us at{" "}
            <a href="mailto:support@octogames.io">support@octogames.io</a>. We may request
            verification of your identity before processing your request.
          </p>

          <h2 className="legal-section-title">13. Legal Basis (EEA/UK Users)</h2>
          <p className="legal-copy">
            Where applicable, we process personal data under legal bases such as performance of a
            contract, legitimate interests, compliance with legal obligations, and consent.
          </p>

          <h2 className="legal-section-title">14. Do Not Track</h2>
          <p className="legal-copy">
            The Website does not currently respond to browser "Do Not Track" signals in a uniform
            way, due to lack of a consistent industry standard.
          </p>

          <h2 className="legal-section-title">15. Changes to This Policy</h2>
          <p className="legal-copy">
            We may update this Privacy Policy from time to time. Updated versions will be posted on
            this page with a revised "Last updated" date. Continued use of the Website after
            changes become effective means you accept the updated policy.
          </p>

          <h2 className="legal-section-title">16. Contact</h2>
          <p className="legal-copy">
            For privacy questions, data requests, or complaints, contact:
            {" "}
            <a href="mailto:support@octogames.io">support@octogames.io</a>.
          </p>
        </article>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
